import { type AgencySource, type JobDepartmentSubRole, type NurseRole, NurseRolePriorityOrder } from "./models";

type AgencySubRoleRow = {
	/** The row's job when job management is on; absent in role-group mode. */
	jobId?: string;
	/** The row's selected role groups when job management is off. */
	roleGroups: string[];
};

type AgencySubRoleParams = {
	catalog: JobDepartmentSubRole[];
	/** Lowercase agency enum value. `null` short-circuits to an empty result. */
	agencySource: AgencySource | null;
	jobManagementEnabled: boolean;
	/** = `chosenDepartment.role_groups` — role group name → role short-codes. */
	roleGroups: Record<string, string[]>;
	row: AgencySubRoleRow;
};

/**
 * Resolves the sub-role options + priority default for posting a shift to an agency, from the
 * per-department sub-role catalog (keyed by job). The catalog is keyed by job. With job management
 * ON the row carries a jobId, so we match on it directly. With job management OFF the row works in
 * role-group mode and has no jobId — but a catalog entry's `subRole` is itself a role short-code,
 * so it's relevant whenever it belongs to one of the row's selected role groups' roles.
 *
 * Pure function shared by the create-shift flow and the post-to-agency dialog/gating. The
 * create-flow's `postToAgency` guard is expressed by the caller passing `agencySource: null`.
 */
export function getAgencySubRoleInfo({ catalog, agencySource, jobManagementEnabled, roleGroups, row }: AgencySubRoleParams): {
	options: NurseRole[];
	defaultValue: NurseRole | undefined;
} {
	if (!agencySource) return { options: [], defaultValue: undefined };
	const sourceMatches = catalog.filter((r) => r.source.toLowerCase() === agencySource);

	let matches: typeof sourceMatches;
	if (jobManagementEnabled) {
		if (!row.jobId) return { options: [], defaultValue: undefined };
		matches = sourceMatches.filter((r) => r.jobId === row.jobId);
	} else {
		const roleGroupRoles = new Set(
			Object.entries(roleGroups)
				.filter(([group]) => row.roleGroups.includes(group))
				.flatMap(([, roles]) => roles),
		);
		matches = sourceMatches.filter((r) => roleGroupRoles.has(r.subRole));
	}

	const options = matches.map((r) => r.subRole);
	// A role group can span multiple jobs, so the catalog may carry several is_default entries
	// (one per job). Pick the highest-priority default, falling back to the highest-priority option.
	const pickByPriority = (roles: NurseRole[]): NurseRole | undefined => NurseRolePriorityOrder.find((r) => roles.includes(r)) ?? roles[0];
	const defaultValue = pickByPriority(matches.filter((r) => r.isDefault).map((r) => r.subRole)) ?? pickByPriority(options);
	return { options, defaultValue };
}
